import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Text } from '@resideo/blueprint-react';
import { GeocodingAddress } from 'utils/geocoding';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(Box)`
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  padding: 24px;
`;

const ModalTitle = styled(Text)`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ModalText = styled(Text)`
  font-size: 0.875rem;
  color: #727272;
  margin-bottom: 16px;
`;

const AddressOption = styled(Box)<{ $isSelected: boolean }>`
  padding: 16px;
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? '#1b6bb3' : '#cccccc')};
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  background-color: ${({ $isSelected }) => ($isSelected ? '#f0f7fc' : 'white')};

  &:hover {
    border-color: #1b6bb3;
  }
`;

const AddressLabel = styled(Text)`
  font-weight: 500;
  margin-bottom: 4px;
`;

const AddressText = styled(Text)`
  font-size: 0.875rem;
  color: #333;
`;

const ButtonContainer = styled(Flex)`
  margin-top: 24px;
  gap: 12px;
  justify-content: flex-end;
`;

interface AddressVerificationModalProps {
  isOpen: boolean;
  originalAddress: GeocodingAddress;
  suggestedAddress: GeocodingAddress | null;
  onClose: () => void;
  onSelectAddress: (address: GeocodingAddress) => void;
}

const formatAddressForDisplay = (address: GeocodingAddress): string => {
  const parts = [
    address.address1,
    address.address2,
    address.city,
    address.state,
    address.zipPostalCode,
    address.countryCode,
  ].filter(Boolean);
  return parts.join(', ');
};

const AddressVerificationModal: React.FC<AddressVerificationModalProps> = ({
  isOpen,
  originalAddress,
  suggestedAddress,
  onClose,
  onSelectAddress,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<
    'original' | 'suggested'
  >('original');

  if (!isOpen) return null;

  const handleSave = () => {
    if (selectedOption === 'suggested' && suggestedAddress) {
      onSelectAddress(suggestedAddress);
    } else {
      onSelectAddress(originalAddress);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalTitle>
          {t('signUp.companyInfo.addressVerification.title')}
        </ModalTitle>
        <ModalText>
          {t('signUp.companyInfo.addressVerification.message')}
        </ModalText>
        <Text marginBottom='medium' fontWeight='500'>
          {t('signUp.companyInfo.addressVerification.chooseOne')}
        </Text>

        <AddressOption
          $isSelected={selectedOption === 'original'}
          onClick={() => setSelectedOption('original')}
          data-test-original-address>
          <AddressLabel>
            {t('signUp.companyInfo.addressVerification.originalAddress')}
          </AddressLabel>
          <AddressText>{formatAddressForDisplay(originalAddress)}</AddressText>
        </AddressOption>

        {suggestedAddress && (
          <AddressOption
            $isSelected={selectedOption === 'suggested'}
            onClick={() => setSelectedOption('suggested')}
            data-test-suggested-address>
            <AddressLabel>
              {t('signUp.companyInfo.addressVerification.suggestedAddress')}
            </AddressLabel>
            <AddressText>
              {formatAddressForDisplay(suggestedAddress)}
            </AddressText>
          </AddressOption>
        )}

        <ButtonContainer>
          <Button variant='secondary' onClick={onClose} data-test-back-button>
            {t('common.back')}
          </Button>
          <Button variant='primary' onClick={handleSave} data-test-save-button>
            {t('common.save')}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddressVerificationModal;
